import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Modal, Space, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import { Option } from "antd/lib/mentions";
import CustomButton from "components/Common/buttons/CustomButton";
import NGIInput from "components/Common/inputs/NGIInput";
import NGISelect from "components/Common/inputs/NGISelect";
import { createSmartScale, getUsersList, updateSmartScale } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
import { AUTH_USER, USER_ROLES, USER_TENANT_NAME } from "utils/constants";
import { uniq } from "lodash";
import { inputPatternTitle, inputTextPattern } from "components/Common/inputs/variables";
import { userHasOneOfRoles } from "utils/helpers/functions";

const AddSmartScaleModal = ({ data, onCancel, updateList }) => {
  const [form] = Form.useForm();
  const [isLoading, setLoader] = useState(false);
  const [depotIsLoading, setDepotIsLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const {
    list: { depot },
  } = useSelector(DictionariesSelector);

  const onFinish = useCallback(
    values => {
      const { assignedTo, serialNumber, depot } = values;
      setLoader(true);
      const request = data ? updateSmartScale : createSmartScale;
      request({ assignedTo, serialNumber, depot }, data?.id)
        .then(() => {
          setLoader(false);
          updateList();
          successMessage(`Smart Scale was ${data ? "updated" : "created"} successfully`);
          onCancel();
        })
        .catch(err => {
          setLoader(false);
          defaultNgiErrorMethod(err);
        });
    },
    [data]
  );

  const onDepotChange = useCallback(
    value => {
      setUsers([]);
      setDepotIsLoading(true);
      form.setFieldsValue({
        assignedTo: null,
      });
      searchUsers(value);
    },
    [form]
  );

  const searchUsers = useCallback(value => {
    getUsersList({
      role: [USER_ROLES.DEPOT_CLERK].join(),
      depot: value,
      pageSize: 1000,
    })
      .then(res => {
        setUsers(res?.data?.result?.items);
        setDepotIsLoading(false);
      })
      .catch(() => {
        setDepotIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data);
      searchUsers(data.depot);
      return;
    }
    if (userHasOneOfRoles([USER_ROLES.DEPOT_CLERK, USER_ROLES.OPERATION_MANAGERS])) {
      form.setFieldsValue({ depot: AUTH_USER()?.depotName });
      searchUsers(AUTH_USER()?.depotName);
    }
  }, []);

  return (
    <Modal open title={`${data ? "Update" : "New"} Smart Scale`} onCancel={onCancel} closable={false} footer={[]}>
      <Spin spinning={isLoading}>
        <Form name="newSmartScale" form={form} onFinish={onFinish}>
          <Col span={24}>
            <Title level={5}>Serial Number</Title>
            <Form.Item name="serialNumber" rules={[{ required: true, message: "Please enter serial number" }]}>
              <NGIInput
                placeholder={"Enter serial number"}
                allowClear
                pattern={inputTextPattern}
                title={inputPatternTitle}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Title level={5}>Depot</Title>
            <Form.Item name="depot" rules={[{ required: true, message: "Please select depot" }]}>
              <NGISelect
                disabled={userHasOneOfRoles([USER_ROLES.DEPOT_CLERK, USER_ROLES.OPERATION_MANAGERS])}
                showSearch
                style={{ width: "100%" }}
                tokenSeparators={[","]}
                size={"default"}
                placeholder={"Select depot"}
                allowClear
                onChange={onDepotChange}
              >
                {depot[USER_TENANT_NAME()] &&
                  uniq(depot[USER_TENANT_NAME()])
                    ?.sort()
                    ?.map(item => (
                      <Option key={item} value={item}>
                        {item}
                      </Option>
                    ))}
              </NGISelect>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Title level={5}>Assigned User</Title>

            <Form.Item name="assignedTo" rules={[{ required: true, message: "Please select assigned user" }]}>
              <NGISelect
                showSearch
                style={{ width: "100%" }}
                tokenSeparators={[","]}
                size={"default"}
                placeholder={"Select assigned user"}
                loading={depotIsLoading}
                allowClear
              >
                {users.map(item => (
                  <Option key={item.email} value={item.email}>
                    {item.email}
                  </Option>
                ))}
              </NGISelect>
            </Form.Item>
          </Col>

          <Space>
            <CustomButton onClick={onCancel} type="primary" color="outlined" size="small" text="Cancel" />
            <CustomButton htmlType="submit" type="primary" size="small" text={data ? "Update" : "Save"} />
          </Space>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddSmartScaleModal;
